export interface CurityConfig {
  baseURL: string;
  clientId: string;
}

export interface AuthorizeCodeParams {
  code: string;
  codeVerifier: string;
  redirectUri?: string;
}

/**
 * Carries the HTTP status and RFC 6749 `error` code so callers can tell a dead
 * session (invalid_grant) from a transient outage (5xx/429) instead of guessing
 * from a formatted message string (ENB-2472).
 */
export class OAuthError extends Error {
  readonly httpStatus: number;
  readonly oauthErrorCode?: string;

  constructor(message: string, httpStatus: number, oauthErrorCode?: string) {
    super(message);
    this.name = 'OAuthError';
    this.httpStatus = httpStatus;
    this.oauthErrorCode = oauthErrorCode;
  }
}

/**
 * Extends `OAuthError` so callers that gate on `instanceof OAuthError` (AuthContext's
 * cold-start `refreshUser()`) classify it instead of treating it as an unrecognised —
 * and therefore terminal — failure. Status 200 with no error code ⇒ transient.
 */
export class EmptyRefreshResponseError extends OAuthError {
  constructor() {
    super('OAuth refresh failed: server returned 200 with empty body', 200);
    this.name = 'EmptyRefreshResponseError';
  }
}

/**
 * Shared by `ServiceTripsApi` (per-request refresh) and `AuthContext`'s cold-start
 * `refreshUser()` — both must classify a refresh failure the same way, or a startup
 * refresh that hits e.g. a Curity 503 gets silently logged out here while a
 * mid-session refresh of the same error would have retried (ENB-2472).
 *
 * Only a definitive rejection of the grant should end the session. Fails open:
 * an error we can't classify (a plain `Error`, a future error type) is treated as
 * transient, not terminal — outage, rate limit, timeout, and gateway errors must
 * not log the user out on the first strike.
 *
 * Per RFC 6749 §5.2 a rejected grant is a 400 carrying `invalid_grant`/`invalid_client`;
 * a 401 on the token endpoint is a client-authentication failure. Both are terminal.
 * TODO(ENB-2472 Q9): tighten once Curity's per-failure status/code contract is documented.
 */
export function isTransientRefreshError(error: unknown): boolean {
  if (error instanceof OAuthError) {
    if (error.oauthErrorCode === 'invalid_grant' || error.oauthErrorCode === 'invalid_client') {
      return false;
    }
    return error.httpStatus !== 400 && error.httpStatus !== 401;
  }
  return true;
}
